// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — what the plans say in words: a plan's name, price, seats and the
 * lines of its card, from its row in `xrPlans` (the one table).
 *
 * Shared by the plans dialog (`xrPlansUI`) and the public price list at
 * `/pricing` (`scripts/build-privacy-page.ts`, owner 2026-10-15), so the
 * two can never say different things. No DOM here: the price list is
 * built in Node.
 */

import { getLocale, t, type MessageKey } from '../i18n'
import { formatNumber } from '../i18n/format'
import { MEETING_MAX_PEOPLE } from '../services/roomProtocol'
import { CHAT_DAILY_DEFAULT, KHIPU_MAX_DEFAULT, STAGE_ADDON_YEARLY, STAGE_MAX_MEMBERS, type XrPackage, type XrPlan, type XrPlanId, type XrSegment } from '../services/xrPlans'

/** An amount in euros in the reader's language, with cents only when there are some. */
export const money = (amount: number): string =>
  amount.toLocaleString(getLocale(), { minimumFractionDigits: Number.isInteger(amount) ? 0 : 2, maximumFractionDigits: 2 })

/** Message keys by id: locale keys are camelCase, ids carry hyphens and underscores. */
export const PLAN_NAME: Record<XrPlanId, MessageKey> = {
  plus: 'xr.plan.plus.name',
  teacher: 'xr.plan.teacher.name',
  school: 'xr.plan.school.name',
  district: 'xr.plan.district.name',
  researcher: 'xr.plan.researcher.name',
  lab: 'xr.plan.lab.name',
  department: 'xr.plan.department.name',
  'situation-team': 'xr.plan.situationTeam.name',
  'situation-team-plus': 'xr.plan.situationTeamPlus.name',
  'situation-org': 'xr.plan.situationOrg.name',
  'media-desk': 'xr.plan.mediaDesk.name',
  newsroom: 'xr.plan.newsroom.name',
  venue: 'xr.plan.venue.name',
}
/** A plan's name in the reader's language. */
export function planName(id: XrPlanId): string {
  return t(PLAN_NAME[id])
}

export const PACKAGE_WORDS: Record<XrPackage, { name: MessageKey; desc: MessageKey }> = {
  'xr-atmosphere': { name: 'xr.package.atmosphere.name', desc: 'xr.package.atmosphere.desc' },
  'xr-ocean': { name: 'xr.package.ocean.name', desc: 'xr.package.ocean.desc' },
  'xr-climate': { name: 'xr.package.climate.name', desc: 'xr.package.climate.desc' },
  'xr-fire-aq': { name: 'xr.package.fireAq.name', desc: 'xr.package.fireAq.desc' },
}
export const SEGMENT_WORDS: Record<XrSegment, { name: MessageKey; lead: MessageKey }> = {
  situation: { name: 'xr.segment.situation.name', lead: 'xr.segment.situation.lead' },
  research: { name: 'xr.segment.research.name', lead: 'xr.segment.research.lead' },
  education: { name: 'xr.segment.education.name', lead: 'xr.segment.education.lead' },
  public: { name: 'xr.segment.public.name', lead: 'xr.segment.public.lead' },
  media: { name: 'xr.segment.media.name', lead: 'xr.segment.media.lead' },
  venue: { name: 'xr.segment.venue.name', lead: 'xr.segment.venue.lead' },
}

/** A daily stream volume in words: "1 GB", "500 MB". */
export function volumeText(mb: number): string {
  return mb >= 1000 ? `${formatNumber(mb / 1000, { maximumFractionDigits: 1 })} GB` : `${formatNumber(mb, { maximumFractionDigits: 0 })} MB`
}

/** What a plan card lists, from its row in the table. */
export function planLines(plan: XrPlan): string[] {
  const lines: string[] = []
  if (plan.meetings > 0) lines.push(t('xr.plans.cap.host', { n: Math.min(plan.meetings, MEETING_MAX_PEOPLE) }))
  if (plan.rooms > 5) lines.push(t('xr.plans.cap.rooms', { n: plan.rooms }))
  if (plan.live) lines.push(t('xr.plans.cap.live'))
  // AYNI (stage plan, ST5/ST6): the stage included, or its add-on's price (sold by hand for now).
  const stageAddon = STAGE_ADDON_YEARLY[plan.id]
  if (plan.stage) lines.push(t('xr.plans.cap.stage', { n: STAGE_MAX_MEMBERS + 1 }))
  else if (stageAddon !== undefined) lines.push(t('xr.plans.cap.stageAddon', { n: STAGE_MAX_MEMBERS + 1, amount: money(stageAddon) }))
  if (plan.activities) lines.push(t('xr.plans.cap.activities'))
  // AYNI (owner, 2026-10-17): Plus plays Khipus and keeps none, as Free.
  if (plan.khipus) lines.push(t(plan.khipuRetention === null ? 'xr.plans.cap.khipusPlay' : 'xr.plans.cap.khipus'))
  // AYNI (owner, 2026-10-05): Education measures with Line and Box and
  // downloads the pins; the other plans add whole-region statistics and
  // every export.
  // AYNI (owner, 2026-10-05, research decision R7): whole-region statistics
  // and every export are separate lines of the ladder, so a card names its
  // own pair.
  // AYNI (owner, 2026-10-06): Plus reads values and pins, without Line,
  // Box or CSV, and its card names its limits.
  if (plan.data) {
    if (!plan.lineBox) {
      lines.push(t('xr.plans.cap.dataPins', { n: plan.pins }))
    } else {
      const key = plan.analysis === 'full'
        ? plan.exports === 'all' ? 'xr.plans.cap.dataFull' : 'xr.plans.cap.dataFullPins'
        : plan.exports === 'all' ? 'xr.plans.cap.dataBoxAll' : 'xr.plans.cap.dataBasic'
      lines.push(t(key))
    }
  }
  if (plan.data) lines.push(plan.globes === 1 ? t('xr.plans.cap.globeOne') : t('xr.plans.cap.globes', { n: plan.globes }))
  if (plan.overlays !== null) lines.push(plan.overlays === 1 ? t('xr.plans.cap.overlayOne') : t('xr.plans.cap.overlays', { n: plan.overlays }))
  if (plan.chatbot) lines.push(plan.chatDaily < CHAT_DAILY_DEFAULT ? t('xr.plans.cap.chatDaily', { n: plan.chatDaily }) : t('xr.plans.cap.chatbot'))
  // AYNI: the research tools, listed as far as they are built (research plan).
  if (plan.research) lines.push(t('xr.plans.cap.research'))
  // AYNI (owner, 2026-10-17): Media's anomalies and figures, without the rest of the research tools.
  else if (plan.anomalies) lines.push(t('xr.plans.cap.anomalies'))
  if (plan.packages !== 'none') {
    lines.push(
      t(
        plan.packages === 'all' ? 'xr.plans.cap.allPackages'
          : plan.packages === 'all-hazards' ? 'xr.plans.cap.hazards'
            : 'xr.plans.cap.liveHighlights',
      ),
    )
  }
  if (plan.streamDailyMB !== null) lines.push(t('xr.plans.cap.streamDaily', { amount: volumeText(plan.streamDailyMB) }))
  if (plan.khipuRetention !== null && plan.khipuMax < KHIPU_MAX_DEFAULT) lines.push(t('xr.plans.cap.khipusKept', { n: plan.khipuMax, days: plan.khipuGraceDays }))
  if (plan.branding) lines.push(t('xr.plans.cap.branding'))
  if (plan.credits > 0) lines.push(t('xr.plans.cap.credits', { n: formatNumber(plan.credits) }), t('xr.plans.cap.creditsRollover'))
  if (plan.segment === 'public') lines.push(t('xr.plans.cap.personal'))
  return lines
}

/**
 * A plan's yearly price in words, or "By quote": Plus with VAT included,
 * Researcher at its academic and commercial prices (owner, 2026-10-17).
 */
export function priceText(plan: XrPlan): string {
  if (plan.yearly === null) return t('xr.plans.byQuote')
  if (plan.commercialYearly !== undefined) return t('xr.plans.researchPrices', { academic: money(plan.yearly), commercial: money(plan.commercialYearly) })
  if (plan.vatIncluded) return t('xr.plans.perYearVat', { amount: money(plan.yearly) })
  return t(plan.from ? 'xr.plans.fromPerYear' : 'xr.plans.perYear', { amount: money(plan.yearly) })
}

/** The lines under a price: Plus's month, Researcher's price with own data (coming soon). */
export function priceNotes(plan: XrPlan): string[] {
  const notes: string[] = []
  if (plan.monthly !== undefined) notes.push(t(plan.vatIncluded ? 'xr.plans.perMonthVat' : 'xr.plans.perMonth', { amount: money(plan.monthly) }))
  if (plan.ownDataYearly) notes.push(t('xr.plans.ownDataSoon', { academic: money(plan.ownDataYearly.academic), commercial: money(plan.ownDataYearly.commercial) }))
  return notes
}

/** Who a plan is for, in seats. */
export function seatsText(plan: XrPlan): string {
  return plan.seats === null ? t('xr.plans.seats.quote') : plan.seats === 1 ? t('xr.plans.seats.one') : t('xr.plans.seats.many', { n: plan.seats })
}
